import {
  ApplicationMIMEType,
  type SupportedContentTypeCategory,
  TextMIMEType,
  convertHeadersToArray,
  createModifiers,
  getContentType,
  getContentTypeCategory,
} from '@carboncopyco/editor-utils';
import { usePrevious } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { object, string } from 'yup';
import {
  CreateDynamicResponse,
  CustomModifierField,
  CustomModifierFieldInfoMessage,
  ResponseTypeCategoryFilter,
  ResponseTypeField,
  StaticResponseField,
  contentReplaceModifiersSchema,
  headersSchema,
  modifiersSchema,
  responseSchema,
  schemaResolver,
} from '../../../TransactionRuleForm';
import {
  BackButton,
  CancelButton,
  FormLayout,
  Section,
  SubmitButton,
} from '../../../TransactionRuleModal';
import { useElementSize } from '../../../hooks';
import { useCreateTransactionRuleContext } from '../../CreateTransactionRuleContext';
import type { TransactionModifiersForm } from '../../types';
import { useModifiersBuilder } from '../../useModifiersBuilder';

const schema = object({
  responseType: string().oneOf(['static', 'dynamic', 'custom']).required(),
  response: responseSchema,
  dynamicResponseModifiers: contentReplaceModifiersSchema,
  headers: headersSchema,
  customModifier: modifiersSchema.optional(),
});

export const CreateTransactionModifiersStep = () => {
  const { editorDataRulesDynamicResponse } = useFlags();
  const {
    selectedTransaction,
    transactionRule,
    updateTransactionRule,
    prevStep,
    cancelCreation,
    nextStep,
    onResponseTypeSelected,
  } = useCreateTransactionRuleContext();

  const responseDefaultValue = useMemo(
    () =>
      !selectedTransaction
        ? ''
        : selectedTransaction?.original.response.body ?? '',
    [selectedTransaction]
  );

  const headersDefaultValue = useMemo(() => {
    return convertHeadersToArray(
      selectedTransaction?.original.response?.headers ?? {}
    );
  }, [selectedTransaction]);

  const form = useForm<TransactionModifiersForm>({
    defaultValues: {
      dynamicResponseModifiers: [],
      response: responseDefaultValue,
      responseType: 'static',
      customModifier: JSON.stringify(
        createModifiers('custom', {
          type: 'MODIFIER_TYPE',
          applyTo: 'response',
          arguments: {},
        })
      ),
      headers: headersDefaultValue,
    },
    resolver: schemaResolver(schema) as any,
  });
  const {
    watch,
    setValue,
    getValues,
    handleSubmit,
    getFieldState,
    resetField,
  } = form;

  const [staticResponseCategory, setStaticResponseCategory] =
    useState<SupportedContentTypeCategory>('json');

  const [isLegalResponse, setIsLegalResponse] = useState<boolean>(true);
  const responseType = watch('responseType');
  const response = watch('response');
  const headers = watch('headers');
  const dynamicResponseModifiers = watch('dynamicResponseModifiers');
  const customModifierState = getFieldState('customModifier');
  const [formattedResponse, setFormattedResponse] = useState<string>(response);
  const contentType = getContentType(headers ?? []);
  const contentTypeCategory = !selectedTransaction
    ? staticResponseCategory
    : getContentTypeCategory(contentType);

  const prevResponseType = usePrevious(responseType);

  const showResponseCategoryFilter =
    !selectedTransaction && responseType === 'static';

  const [containerRef, { height }] = useElementSize();

  const { buildModifiers } = useModifiersBuilder({
    getFormValues: () => getValues(),
  });

  const submitForm = () => {
    const updatedTransactionRule = {
      ...transactionRule,
      modifiers: buildModifiers(),
    };

    updateTransactionRule(updatedTransactionRule);
    return nextStep();
  };

  useEffect(() => {
    // Clean state when switching between response types.
    if (prevResponseType !== responseType) {
      setValue('dynamicResponseModifiers', []);
      setValue('response', responseDefaultValue);
      setValue('customModifier', form.formState.defaultValues.customModifier);
      setIsLegalResponse(true);
    }
  }, [responseType]);

  useEffect(() => {
    // Set default content-type header for static response when selector is defined from scartch.
    if (!selectedTransaction && staticResponseCategory !== 'image') {
      setValue('headers', [
        {
          name: 'content-type',
          value:
            staticResponseCategory === 'text'
              ? TextMIMEType.TextPlain
              : ApplicationMIMEType.ApplicationJSON,
        },
      ]);
    }
  }, [selectedTransaction, contentTypeCategory]);

  useEffect(() => {
    onResponseTypeSelected([responseType, contentTypeCategory]);
  }, [contentTypeCategory, responseType]);

  const isNextButtonDisabled = () => {
    if (responseType === 'custom') {
      return (
        customModifierState.invalid ||
        !customModifierState.isDirty ||
        !isLegalResponse
      );
    }

    if (responseType === 'dynamic') {
      return !dynamicResponseModifiers?.length;
    }

    return !isLegalResponse || !response?.trim()?.length;
  };

  const fieldsToComponentMap = {
    static: () => (
      <StaticResponseField
        hideLabel
        onFormat={(value) => setFormattedResponse(value)}
        onError={(errors) => setIsLegalResponse(!errors.length)}
        height={height}
        staticResponseCategory={
          (!selectedTransaction && staticResponseCategory) || null
        }
      />
    ),
    dynamic: () => (
      <CreateDynamicResponse
        response={formattedResponse}
        hidePreview={!selectedTransaction}
      />
    ),
    custom: () => (
      <CustomModifierField
        onError={(errors) => setIsLegalResponse(!errors.length)}
      />
    ),
  };

  return (
    <FormProvider {...form}>
      <FormLayout onSubmit={handleSubmit(submitForm)}>
        <FormLayout.Content>
          <Section
            title="Edit data"
            iconType="modifiers"
            overflowHidden={responseType === 'dynamic'}
          >
            <div className="flex">
              <div className="flex flex-1 justify-between items-center">
                {editorDataRulesDynamicResponse && (
                  <ResponseTypeField
                    disabledField={
                      (contentTypeCategory === 'image' && 'dynamic') || null
                    }
                  />
                )}
                {responseType === 'custom' && (
                  <div className="flex items-center">
                    <CustomModifierFieldInfoMessage />
                  </div>
                )}
              </div>
            </div>
            {showResponseCategoryFilter && (
              <ResponseTypeCategoryFilter
                contentTypeCategory={staticResponseCategory}
                onChange={(filter) => {
                  resetField('headers');
                  resetField('response');
                  setStaticResponseCategory(filter);
                }}
              />
            )}
            <div
              className="flex flex-col flex-1 min-h-0 "
              ref={containerRef}
            >
              {fieldsToComponentMap[responseType]()}
            </div>
          </Section>
        </FormLayout.Content>
        <FormLayout.Footer>
          <BackButton onClick={prevStep} />
          <div className="flex-1" />
          <CancelButton onClick={cancelCreation} />
          <SubmitButton
            label="Next"
            disabled={isNextButtonDisabled()}
          />
        </FormLayout.Footer>
      </FormLayout>
    </FormProvider>
  );
};
